import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import heroImage from "@/assets/concrete-repairs-totowa-nj.webp";
import spalledImage from "@/assets/spalled-concrete-step-damage-closeup-totowa-nj.webp";
import crackRepairImage from "@/assets/concrete-driveway-crack-repair-totowa-nj.webp";
import walkwayImage from "@/assets/repaired-concrete-walkway-finished-totowa-nj.webp";
import homeownerImage from "@/assets/homeowner-inspecting-patio-crack-totowa-nj.webp";

const faqs = [
  {
    question: "How quickly can you get someone out to look at a cracked driveway in Totowa?",
    answer: "We can usually schedule a visit within a few days, sooner if the crack is a trip hazard. Concrete problems don't fix themselves, and cracks that hold water tend to get worse fast once freeze-thaw cycles start. If you've got a lip that catches a shoe or stroller wheel, tell us that when you call so we can bump you up the schedule. Most driveways, walkways, and patios in Totowa can be evaluated on-site the same visit.",
  },
  {
    question: "What happens when you come out to inspect my concrete?",
    answer: "We walk the slab with you, check crack width, look for standing water, and see how the surface has held up around joints and edges. We'll use the season-to-season checks, like measuring a crack against a coin or ruler, to judge whether it's still moving. Then we explain what's driving the damage, whether it's soil movement, drainage, or freeze-thaw, and what your realistic options are before any work starts.",
  },
  {
    question: "Why does Totowa concrete crack so much more in winter?",
    answer: "Totowa sits on clay-heavy soil that swells and shrinks with moisture, and that movement pushes straight up into your slab. Add in freeze-thaw cycling, where trapped water expands every time temperatures drop below freezing, and de-icing salt that wears down the surface, and you've got three forces working on your concrete at once. A small crack in October can turn into a real problem by February if nothing addresses what's happening underneath it.",
  },
  {
    question: "How do I know if a crack is just cosmetic or a problem?",
    answer: "A crack is usually cosmetic if it's thinner than a credit card and hasn't changed in a year or two. Once a crack starts widening after storms, holds standing water, runs toward a foundation wall, or creates an uneven lip, it's a sign of ongoing movement underneath. Snap a photo now with a coin for scale, then check it again after the next freeze-thaw cycle. If it's grown, it needs a professional look, not just filler.",
  },
  {
    question: "Is there a bad time of year to repair concrete in Totowa?",
    answer: "Late fall through mid-winter is the riskiest time to leave a crack alone, since repeated freezing and thawing pushes small cracks into bigger ones. Repairs hold up best when temperatures are above freezing and the slab has time to cure without another hard freeze right behind it. That said, sunken sections and drainage issues shouldn't wait for perfect weather, especially if water is pooling and turning to ice on steps or walkways.",
  },
  {
    question: "Will a sunken section of my walkway keep getting worse if I ignore it?",
    answer: "Yes, a sunken slab almost always points to erosion or a void underneath, and that gap tends to grow over time. The concrete on top isn't broken, it's just lost its support, so patching the surface won't stop the sinking. Left alone, it usually widens the gap between slabs, creates a bigger trip hazard, and lets more water collect underneath, which speeds up the damage headed into another Totowa winter.",
  },
];

export default function ConcreteRepair() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Concrete Repairs Totowa, NJ | Masonry Pro Construction</title>
        <meta name="description" content="Cracked or sinking concrete in Totowa? We repair driveways, walkways, and patios across North Jersey. Free estimates, no trip fees. Call Masonry Pro today." />
        <meta property="og:description" content="Cracked or sinking concrete in Totowa? We repair driveways, walkways, and patios across North Jersey. Free estimates, no trip fees. Call Masonry Pro today." />
        <meta property="og:title" content="Concrete Repairs Totowa, NJ | Masonry Pro Construction" />
        <meta property="og:image" content="https://masonry-pro.com/og/concrete-general.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/concrete-general.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden bg-navy-dark pt-40 pb-20">
            <img
              src={heroImage}
              alt="Concrete repair work on a residential driveway in Totowa, NJ"
              width={1920}
              height={1080}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-navy-dark/70 to-navy-dark/85" />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-3xl">
                <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6 text-foreground">
                  Concrete Repairs in <span className="text-gradient-gold">Totowa, NJ</span> Serving North Jersey: Stop Cracks Before Winter Makes Them Worse
                </h1>
                <p className="text-lg md:text-xl text-muted-foreground">
                  Cracked, sunken, or spalling concrete gets worse every freeze-thaw cycle. We find out what is happening under the slab, then repair driveways, walkways, patios, and steps across North Jersey.
                </p>
              </div>
            </div>
          </section>

          <section className="py-16 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">

                {/* Section 1 */}
                <div className="mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Signs You Need <span className="text-gradient-gold">Concrete Repair</span>
                  </h2>

                  <div className="mb-8 p-8 bg-muted/50 rounded-xl border border-border">
                    <h3 className="text-xl font-bold mb-3">Quick Summary</h3>
                    <p className="text-lg text-muted-foreground">
                      Concrete rarely fails without warning. Most problems leave visible clues long before they turn expensive or dangerous. Walk your property and check for uneven surfaces, wide cracks, flaking, sunken spots, and pooling water, and you'll have a pretty clear picture of whether repair can wait or needs to happen now.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                    <div className="rounded-xl overflow-hidden border border-border lg:order-1">
                      <img
                        src={spalledImage}
                        alt="Close-up of spalled and flaking concrete step damage in Totowa, NJ"
                        width={1024}
                        height={1024}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-auto object-cover"
                      />
                    </div>
                    <div className="lg:order-2">
                      <div className="space-y-4 text-lg text-muted-foreground">
                        <p>
                          Concrete holds up well, but it isn't immune to time, weather, shifting soil, and everyday wear. Before you call anyone, take a few minutes to walk your <Link to="/concrete-services/driveways" className="text-secondary hover:underline">driveway</Link>, <Link to="/concrete-services/sidewalks" className="text-secondary hover:underline">walkway</Link>, patio, or garage floor. Look at the surface in good daylight, run your hand along any cracks you find, and note where water tends to sit after it rains. The signs below are the ones that usually mean a slab needs a professional look rather than a quick DIY patch.
                        </p>
                      </div>

                  <ul className="list-disc pl-6 space-y-3 text-lg text-muted-foreground mt-6">
                    <li><span className="font-semibold text-foreground">Trip-hazard unevenness:</span> When one section of a slab sits noticeably higher or lower than the piece next to it, even by just half an inch, it forms a lip that can catch a shoe or a wheelchair wheel. This is usually caused by soil settling unevenly underneath the concrete, and it tends to get worse over time.</li>
                    <li><span className="font-semibold text-foreground">Wide or spreading cracks:</span> Hairline cracks are common and mostly cosmetic, but cracks wider than a quarter inch, cracks that keep growing, or cracks you can see daylight or soil through mean the slab's structural integrity has been compromised.</li>
                    <li><span className="font-semibold text-foreground">Spalling or flaking surfaces:</span> When the top layer of concrete starts peeling, flaking, or crumbling away and exposes rougher aggregate underneath, it's usually a sign of freeze-thaw damage, deicing salt, or a finishing issue from the original pour.</li>
                    <li><span className="font-semibold text-foreground">Sunken slab sections:</span> A section of walkway or driveway that has dropped relative to the concrete around it usually points to erosion, poor compaction, or a void that's formed underneath the slab.</li>
                    <li><span className="font-semibold text-foreground">Standing water on the slab:</span> If puddles keep forming in the same spots after every rain instead of draining toward the yard or street, the slab has likely lost its proper pitch, which can lead to ice buildup in winter and speed up deterioration.</li>
                  </ul>

                      <p className="text-lg text-muted-foreground mt-6">
                        Any single sign on this list is worth keeping an eye on, but two or more together is a strong hint that the support beneath the concrete has shifted, and a surface patch alone won't fix it. Catching these problems early usually means a smaller, less invasive repair now instead of a full slab replacement later.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section 2 */}
                <div className="mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Why North Jersey Winters <span className="text-gradient-gold">Crack Your Concrete</span>
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                    <div className="rounded-xl overflow-hidden border border-border lg:order-2">
                      <img
                        src={crackRepairImage}
                        alt="Mason repairing a cracked concrete driveway in Totowa, NJ"
                        width={1024}
                        height={1024}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-auto object-cover"
                      />
                    </div>
                    <div className="lg:order-1">
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Concrete doesn't crack just because it's old. In Totowa, it cracks because of what's happening underneath it every single winter.
                    </p>
                    <p>
                      Water works its way into tiny pores in the concrete and into the joints between slabs. Then the temperature drops below freezing, and that trapped water expands. That's freeze-thaw cycling, and North Jersey doesn't get it once a season, it gets it over and over. Each round of freezing and thawing pushes a little harder against the concrete from the inside.
                    </p>
                    <p>
                      Then there's the soil. Much of Totowa sits on clay-heavy ground, and clay swells when it's wet and shrinks when it dries out. That movement travels straight up into your slab. We've seen driveways where the crack pattern practically maps the spots where the soil underneath is shifting the most.
                    </p>
                  </div>

                  <div className="space-y-4 text-lg text-muted-foreground mt-4">
                    <p>
                      Salt is the third factor. It melts ice fast, which is great for safety, but it's hard on concrete. <a href="https://www.pacaweb.org/blog/protecting-concrete-from-deicer-damage" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">Salt accelerates freeze-thaw damage and wears away the surface layer</a>, especially on steps and stoops where people track it in and out all winter long.
                    </p>
                  </div>

                  <ul className="list-disc pl-6 space-y-3 text-lg text-muted-foreground mt-6">
                    <li><span className="font-semibold text-foreground">Freeze-thaw cycling:</span> water in the concrete expands and contracts, widening small cracks into big ones</li>
                    <li><span className="font-semibold text-foreground">Clay soil movement:</span> swelling and shrinking soil shifts the slab from below</li>
                    <li><span className="font-semibold text-foreground">Salt exposure:</span> de-icing salt speeds up surface breakdown, especially on steps and walkways</li>
                  </ul>

                  <p className="text-lg text-muted-foreground mt-6">
                    Here's the thing: it's rarely just one of these forces at work, it's usually all three, working together. A hairline crack from last spring can turn into a real trip hazard by next winter if nobody addresses the water sitting underneath it.
                  </p>
                    </div>
                  </div>
                </div>

                {/* Section 3 */}
                <div className="mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    When a Crack Can Wait, and <span className="text-gradient-gold">When It Can't</span>
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                    <div className="rounded-xl overflow-hidden border border-border lg:order-1">
                      <img
                        src={walkwayImage}
                        alt="Repaired concrete walkway with a smooth finish at a Totowa, NJ home"
                        width={1024}
                        height={1024}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-auto object-cover"
                      />
                    </div>
                    <div className="lg:order-2">
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Not every crack in a Totowa driveway or walkway spells disaster. Concrete is rigid by nature, and hairline cracks show up as it cures and settles over time, particularly in older sections of Totowa and neighboring Little Falls where soil moisture swings with the seasons. If a crack is thinner than a credit card, hasn't budged in a year or two, and sits on a low-traffic patio or garden path, a cosmetic polyurethane filler from the hardware store will usually keep water out and buy you several more years of service.
                    </p>
                    <p>
                      Things change quickly once a crack stops sitting still. A crack that's widening, holding standing water after a Passaic County rainstorm, showing up within a few feet of a <Link to="/concrete-services/foundation-repair" className="text-secondary hover:underline">foundation wall</Link>, or creating a lip that catches a shoe or stroller wheel isn't cosmetic anymore. Those are signs of ongoing movement, drainage failure, or soil erosion under the slab, and filling one without fixing the cause usually means you're redoing the patch within a season.
                    </p>
                  </div>

                  <ul className="list-disc pl-6 space-y-3 text-lg text-muted-foreground mt-6">
                    <li>Hairline width, no vertical offset between the two sides, no change over 12 to 24 months: likely cosmetic</li>
                    <li>Crack widens noticeably after freeze-thaw cycles or heavy rain: needs evaluation</li>
                    <li>Water pools in or beside the crack instead of draining away: needs evaluation</li>
                    <li>Crack runs toward or connects with a foundation, garage slab edge, or retaining wall: needs evaluation</li>
                    <li>One side of the crack sits higher than the other, even slightly: trip hazard, address promptly</li>
                    <li>Crack appears alongside cracks in interior drywall or sticking doors: possible foundation involvement</li>
                  </ul>

                  <div className="my-8 p-8 bg-muted/50 rounded-xl border border-border">
                    <h3 className="text-xl font-bold mb-3">The Season-to-Season Test</h3>
                    <p className="text-lg text-muted-foreground">
                      Snap a photo of the crack now with a ruler or coin next to it for scale, then check it again after the next freeze-thaw cycle or heavy storm. If the width, length, or height difference has grown, the slab is still moving and filler alone won't hold. If it looks the same months later, it's likely stable and safe to treat as cosmetic.
                    </p>
                  </div>

                  <p className="text-lg text-muted-foreground">
                    Totowa's clay-heavy soil and freeze-thaw winters make slab movement more common here than in the sandier parts of New Jersey, which is why a crack that looked minor in October can visibly worsen by March. When in doubt, especially near a foundation or on a walking surface, it's worth having a professional check what's happening beneath the slab rather than guessing.
                  </p>
                    </div>
                  </div>
                </div>

                {/* Section 4 */}
                <div className="mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    The Concrete Repair Process, <span className="text-gradient-gold">Step by Step</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      A solid concrete repair job in Totowa tends to follow the same basic sequence every time, and knowing that sequence is what separates homeowners who can spot a shortcut from those who can't. It starts with a physical walk-through of the affected slab, walkway, driveway, or foundation area. A technician looks for cracking patterns, spalling, pooling water, and unevenness between adjoining sections. In older Totowa neighborhoods sitting on clay-heavy soil near the Passaic River basin, this early look often turns up settlement issues that a surface patch would never actually solve.
                    </p>
                    <p>
                      Diagnosis comes next, and this is where real experience shows. Settlement problems, caused by soil compaction or erosion under the slab, look nothing like surface damage such as scaling, spalling, or freeze-thaw cracking, and both are different from drainage failures where water is collecting against a foundation or under a slab edge. Mix one up with another and the repair is likely to fail within a season. A contractor who knows what they're doing will probe the soil, check grading and downspout discharge points, and sometimes run a moisture meter before settling on a repair method.
                    </p>
                    <p>
                      Once the cause is clear, the right method gets chosen. The main options are:
                    </p>
                  </div>

                  <ul className="list-disc pl-6 space-y-3 text-lg text-muted-foreground my-6">
                    <li><span className="font-semibold text-foreground">Surface patching</span> for shallow cracks, spalling, or cosmetic damage that hasn't touched the structural integrity</li>
                    <li><span className="font-semibold text-foreground">Resurfacing</span> with a bonding overlay when the slab is still structurally sound but worn, pitted, or discolored over a wide area</li>
                    <li><span className="font-semibold text-foreground">Polyurethane slab lifting</span> to raise sunken concrete back to grade without tearing it out, often used on settled walkways and driveway aprons</li>
                    <li><span className="font-semibold text-foreground">Full section replacement</span> when the cracking runs deep, reinforcement has failed, or the damage covers a large stretch of the slab</li>
                  </ul>

                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      After the method is settled, a written estimate should spell out materials, labor, expected timeline, and cost, so nothing catches you off guard once work starts. From there, the job gets scheduled around the weather, since curing concrete is picky about temperature and humidity, and Totowa's seasonal swings mean spring and fall usually offer the best windows for repair work. Repairs are one part of the <Link to="/concrete-services" className="text-secondary hover:underline">concrete work we handle across North Jersey</Link>, so the same crew and the same base standards apply whether we're patching a slab or pouring a new one.
                    </p>
                    <p>
                      During the repair itself, every layer, from soil compaction and forming to the bonding agent and finish coat, should be photographed. That's not just a nice customer touch. It's proof that the prep work happened the way it should have beneath the surface, which matters a great deal if a warranty claim ever comes up. Finally, cure time gets respected before the area goes back into full use, typically 24 to 48 hours before foot traffic and up to seven days before vehicles return to a repaired driveway.
                    </p>
                  </div>
                </div>

                {/* Section 5 */}
                <div className="mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    What Affects <span className="text-gradient-gold">Concrete Repair Costs</span>
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                    <div className="rounded-xl overflow-hidden border border-border lg:order-2">
                      <img
                        src={homeownerImage}
                        alt="Homeowner inspecting a cracked concrete patio in Totowa, NJ"
                        width={1024}
                        height={1024}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-auto object-cover"
                      />
                    </div>
                    <div className="lg:order-1">
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Every concrete repair job is different, so we can't give you a real number until we've seen the slab. What drives the price is how deep the damage actually goes, not just what it looks like on the surface.
                    </p>
                    <p>
                      A hairline crack on a sidewalk is a different animal than a sunken section of driveway sitting on washed-out base. The first might just need patching. The second means pulling out the old concrete, rebuilding the base, and possibly adding drainage underneath so the problem doesn't come back.
                    </p>
                  </div>

                  <ul className="list-disc pl-6 space-y-3 text-lg text-muted-foreground mt-6">
                    <li>How much of the slab is affected, and whether it's cracked, sunken, or heaving</li>
                    <li>What's underneath: soil movement, poor base compaction, or a drainage issue feeding water under the slab</li>
                    <li>Access to the work area, since tight side yards and narrow gates slow a crew down</li>
                    <li>Whether rebar or wire mesh needs to be added or replaced for structural strength</li>
                    <li>Disposal and hauling of the old concrete once it's broken out</li>
                    <li>Time of year, since concrete work slows in freezing temps and picks back up in spring</li>
                  </ul>

                  <div className="space-y-4 text-lg text-muted-foreground mt-6">
                    <p>
                      Where the damage traces back to what the slab is sitting on, the fix can move into <Link to="/concrete-services/footings" className="text-secondary hover:underline">footing and foundation work</Link> rather than surface repair, and that's a different conversation about scope and cost.
                    </p>
                    <p>
                      One more thing worth saying plainly: a patch job that ignores the water problem underneath will crack again, guaranteed. So if a contractor quotes you without asking about drainage or soil conditions, that's worth noticing.
                    </p>
                    <p>
                      Ask any contractor what's actually driving their number, whether it's base repair, drainage, rebar, or just labor and materials. If they can't break it down for you, keep asking.
                    </p>
                  </div>

                  <div className="mt-8 p-8 bg-muted/50 rounded-xl border border-border">
                    <h3 className="text-xl font-bold mb-3">Good to know</h3>
                    <p className="text-lg text-muted-foreground">
                      We give free estimates with no trip fees, so you can find out exactly what your repair needs before you commit to anything.
                    </p>
                  </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-20 bg-background">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Frequently Asked <span className="text-gradient-gold">Questions</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-card p-6 rounded-xl border border-border">
                    <h3 className="text-lg font-semibold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="concrete" />

          {/* CTA Section */}
          <CTASection serviceName="Concrete Repair" heading="Cracked or Sinking" highlightedHeading="Concrete?" />
        </main>

        <Footer />
      </div>
    </>
  );
}
